import { useEffect, useRef } from 'react';

import type { PrismaMood } from '../../lib/prisma-status.js';

/**
 * Prisma's Orb (ported from the Prisma app's Orb): a crystal, a six-sided band
 * with short points above and below, drawn three times in her colours (her
 * hair's purple and hot pink, her eyes' blue), all turning together and offset
 * a little side to side. It breathes while she thinks, stirs while she works
 * and ripples while she writes back.
 */

type V3 = [number, number, number];

const CORNERS: V3[] = [];
for (const y of [0.22, -0.22]) {
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    CORNERS.push([Math.cos(a) * 0.82, y, Math.sin(a) * 0.82]);
  }
}
CORNERS.push([0, 0.77, 0], [0, -0.77, 0]);
const FACES: number[][] = [];
for (let i = 0; i < 6; i++) {
  const j = (i + 1) % 6;
  FACES.push([i, i + 6, j + 6, j]); // the band
  FACES.push([12, i, j]); // the top point
  FACES.push([13, j + 6, i + 6]); // the bottom point
}

/** Hue, saturation (%), side offset (fraction of size), opacity. */
const LAYERS: [number, number, number, number][] = [
  [277, 75, 0, 0.34],
  [336, 92, -0.06, 0.26],
  [210, 55, 0.06, 0.26],
];

export function PrismaOrb({ mood, size = 56 }: { mood: PrismaMood; size?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const moodRef = useRef(mood);
  moodRef.current = mood;

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    let raf = 0;
    let t = 0;
    let level = 0;
    const draw = () => {
      t += 0.016;
      const m = moodRef.current;
      // No voice here: her activity drives how much the band swells.
      const target = m === 'writing' ? 0.45 + Math.sin(t * 9) * 0.25 : m === 'working' ? 0.3 + Math.sin(t * 3) * 0.1 : 0;
      level += (Math.min(1, Math.max(0, target)) - level) * 0.12;
      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      ctx.clearRect(0, 0, w, h);
      const base = Math.min(w, h) * 0.24;
      const pulse = m === 'thinking' ? Math.sin(t * 4) * 0.04 : 0;
      const spin = t * (m === 'idle' ? 0.35 : 0.6);
      const tilt = 0.09 + Math.sin(t * 0.45) * 0.025;
      const s = base * (1 + pulse);
      const pts = CORNERS.map(([x, y, z], i) => {
        const band = i < 12;
        const wob = band
          ? Math.sin(t * 1.3 + i * 2.1) * (0.1 + level * 0.32) + Math.cos(t * 0.9 - i * 1.3) * 0.06 + Math.sin(t * 2.1 + i * 0.7) * 0.03
          : 0;
        const k = 1 + wob;
        let X = x * k;
        let Y = y;
        let Z = z * k;
        const cs = Math.cos(spin);
        const sn = Math.sin(spin);
        [X, Z] = [X * cs - Z * sn, X * sn + Z * cs];
        const ct = Math.cos(tilt);
        const st = Math.sin(tilt);
        [Y, Z] = [Y * ct - Z * st, Y * st + Z * ct];
        const persp = 1 / (1 + Z * 0.18);
        return { x: X * s * persp, y: -Y * s * persp, z: Z };
      });
      const order = FACES.map((f, i) => ({ f, i, z: f.reduce((a, j) => a + pts[j]!.z, 0) / f.length })).sort((a, b) => b.z - a.z);
      ctx.globalCompositeOperation = 'lighter';
      for (const [hue, sat, shift, alpha] of LAYERS) {
        const ox = cx + shift * s;
        const oy = cy;
        for (const { f, i } of order) {
          ctx.beginPath();
          f.forEach((j, n) => {
            const p = pts[j]!;
            const q = pts[f[(n + 1) % f.length]!]!;
            if (n === 0) ctx.moveTo(ox + p.x, oy + p.y);
            const mx = (p.x + q.x) / 2;
            const my = (p.y + q.y) / 2;
            const upright = f.length === 4 && j < 6 !== f[(n + 1) % f.length]! < 6;
            const bow = upright ? Math.sin(t * 1.2 + i * 1.7 + n) * (0.07 + level * 0.18) : 0;
            ctx.quadraticCurveTo(ox + mx * (1 + bow), oy + my * (1 + bow), ox + q.x, oy + q.y);
          });
          ctx.closePath();
          const g = ctx.createRadialGradient(ox, oy, base * 0.2, ox, oy, base * 1.45);
          g.addColorStop(0, `hsla(${hue}, ${sat}%, ${i % 2 === 0 ? 72 : 64}%, ${alpha})`);
          g.addColorStop(1, `hsla(${hue}, ${sat}%, 50%, 0)`);
          ctx.fillStyle = g;
          ctx.fill();
          ctx.strokeStyle = `hsla(${hue}, ${Math.min(100, sat + 10)}%, 66%, ${Math.min(1, alpha * 2.2)})`;
          ctx.lineWidth = Math.max(1, w / 210);
          ctx.stroke();
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  // Drawn at twice the shown size for sharp edges.
  return <canvas ref={ref} className={`prisma-orb is-${mood}`} width={size * 2} height={size * 2} style={{ width: size, height: size }} aria-hidden="true" />;
}
